import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Inventory2Icon } from '@/assets';
import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { ChatsRoute } from '@/features/chats/navigation/chatsRoute';
import { ChatListItem } from '@/features/profile-setting/components/ChatListItem';
import type { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { StateView } from '@/shared/components/state-view/StateView';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  gradient: { flex: 1 },
  list: { marginVertical: 0 },
  empty: { paddingHorizontal: 16 }
});

type Props = AppNativeStackScreenProps<typeof ProfileSettingRoute.Chats>;

export const ChatsScreen: FC<Props> = ({ navigation }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.chats'
  });

  const query = useGetOwnChats();
  const { data, isPending, refetch } = query;

  const hasArchivedChats = data?.some((c) => c.archived);

  const unreadMessageCountArchive = data
    ?.filter((c) => c.archived)
    .reduce((sum, chat) => sum + (chat.unreadMessageCount ?? 0), 0);

  const chats = data?.filter((c) => !c.archived);

  useRefetchOnFocus(query);

  useEffect(() => {
    if (isPending) return;
    navigation.setOptions({
      header: ({ navigation }) => (
        <NavigationBar
          title={t('title')}
          buttons={
            hasArchivedChats
              ? [
                  {
                    name: t('archive'),
                    icon: Inventory2Icon,
                    onPress: () => {
                      navigation.push(ChatsRoute.ChatArchive);
                    },
                    badge: unreadMessageCountArchive
                  }
                ]
              : []
          }
        />
      )
    });
  }, [hasArchivedChats, isPending, navigation, t, unreadMessageCountArchive]);

  return (
    <LinearGradient
      style={styles.gradient}
      colors={[
        theme.colors.surfaceContainer,
        theme.colors.secondaryContainer,
        theme.colors.secondaryContainer
      ]}
    >
      <StateView
        isLoading={isPending}
        onRefetch={refetch}
        backgroundColor="transparent"
      >
        {hasItems(chats) ? (
          chats?.map((chat) => <ChatListItem key={chat.id} chat={chat} />)
        ) : (
          <EmptyCard style={styles.empty} description={t('empty')} />
        )}
      </StateView>
    </LinearGradient>
  );
};
